import { type ReactElement, useState } from 'react';

import { useSession } from '@/entities/session';
import { errorCodeOf } from '@/shared/api';

import { gateNextPath } from '../lib/gate-paths';
import { useRequestDownload } from '../model/use-request-download';
import { DownloadGateModal } from './download-gate-modal';

/**
 * 사용자 언어로 옮긴 실패 사유 — 코드가 그대로 보이면 고장으로 읽힌다.
 * 서버 오류가 없는데 실패했다면(error 가 null) 받은 링크를 열지 못한 것이다.
 */
export function downloadErrorMessage(error: unknown, isLink = false): string {
  const code = errorCodeOf(error);
  if (code === 'UNAUTHORIZED') {
    return '로그인이 필요합니다. 다시 로그인해 주세요.';
  }
  if (code === 'NOT_FOUND') {
    return '지금은 받을 수 없는 자료입니다.';
  }
  return isLink ? '링크를 열지 못했습니다.' : '다운로드에 실패했습니다.';
}

/**
 * 받기 버튼 — 다운로드 게이트의 진입점 (LIB-U-03, D3).
 *
 * 비로그인: 안내 모달 → 로그인/가입(next 보존) → 복귀 후 자동 재개(useAutoDownload).
 * 로그인: requestDownload → 성공 시 파일은 서명 URL 로 navigate 저장, 링크는 새 탭으로 연다.
 * 실패는 버튼 옆에 사유 + 재시도로 남긴다 (LIB-U-04 AC-2) — 조용히 사라지면 고장이다.
 */
export function AssetDownloadButton({
  assetId,
  resumePath,
  isLink = false,
  label = isLink ? '열기' : '받기',
  disabled = false,
}: {
  assetId: string;
  /** 로그인 복귀 후 돌아올 현재 페이지 경로 (예: /library/ai-work-5) */
  resumePath: string;
  /** 링크 자료 — 저장 대신 새 탭으로 연다 */
  isLink?: boolean;
  label?: string;
  disabled?: boolean;
}): ReactElement {
  const member = useSession((state) => state.member);
  const [isGateOpen, setGateOpen] = useState(false);
  const download = useRequestDownload();

  const handleClick = (): void => {
    if (member === null) {
      setGateOpen(true);
      return;
    }
    download.mutate({ assetId, openInNewTab: isLink });
  };

  return (
    <div className="flex shrink-0 flex-col items-end gap-1.5">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || download.isPending}
        className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-[13px] font-semibold text-primary-foreground hover:bg-primary/85 disabled:opacity-50"
      >
        {download.isPending ? '준비 중…' : label}
      </button>

      {download.isError ? (
        <p role="alert" className="flex items-center gap-2 text-[12px] text-destructive">
          {downloadErrorMessage(download.error, isLink)}
          <button
            type="button"
            onClick={() => download.mutate({ assetId, openInNewTab: isLink })}
            className="underline underline-offset-2"
          >
            재시도
          </button>
        </p>
      ) : null}

      {isGateOpen ? (
        <DownloadGateModal
          next={gateNextPath(resumePath, assetId)}
          onClose={() => setGateOpen(false)}
        />
      ) : null}
    </div>
  );
}
